import { Dao } from '../../dao';
import {
  IGizmo,
  IGizmoData,
  IIndex,
  ISearchFeatures,
  ISearchResult,
} from '../../types';
import Gizmo from '../gizmo';
import { selectGizmoSearchResult } from '.';

interface IParams {
  data: IGizmoData | IGizmoData[];
  previousData?: IGizmoData | IGizmoData[];
  context: {
    gizmo: Gizmo;
    /**
     * Dao instance
     */
    dao: Dao;
    /**
     * Gizmo callbacks used to get information from the parent RootGizmo
     */
    // callbacks: IGizmoCallbacks;
    /**
     * Function used to update the searchResultsWithData cache
     */
    setSearchResultsWithData: (
      searchResultsWithData: {
        searchResult: ISearchResult;
        // data: IFilledFormRowItem;
        data: any;
      }[]
    ) => void;
    /**
     * Reference to the Gizmo's searchFeatures instance variable, basically it's used to know
     * when to send an event to clear the updated gizmos
     */
    gizmoSearchFeatures: ISearchFeatures;
    /**
     * Callback function used to update the search features present in the Gizmo,
     * on the callback implementation we're updating the searchFeatures instance
     * variable and we're going to send data through the observable in order to update
     * the platform layer.
     */
    setGizmoSearchFeatures: (searchFeatures: ISearchFeatures) => void;
  };
}

/**
 * Function that will be called by the Gizmo class in order to run setData specific code,
 * right now that code is only used for the gizmoSearch functionality
 * @param {IGizmoData} data data that's being set
 * @param {IGizmoData} previousData previous data that was set
 * @returns
 */
function setData({ data, previousData, context }: IParams): Promise<void> {
  return new Promise<void>(async resolve => {
    const {
      gizmo,
      dao,
      gizmoSearchFeatures,
      setGizmoSearchFeatures,
      setSearchResultsWithData,
    } = context;

    const config = gizmo.getConfig();

    let search = config.ops?.search?.fluxSearch;
    if (!search) {
      search = config.ops?.search?.textSearch;
    }

    // If search is configured we enter this if
    if (
      // Right now we're only enabling this for text_fields
      config.type === 'text_field' &&
      config.ops?.search?.minChars &&
      search &&
      config.ops.search.columns &&
      gizmo &&
      dao &&
      !Array.isArray(data) &&
      data.v
    ) {
      const value = data.v.toString();
      let dataChanged = true;

      // If previousData is defined we check if the value has changed
      if (
        previousData &&
        !Array.isArray(previousData) &&
        previousData.v === value
      ) {
        // If it didn't change it means we're selecting a result
        dataChanged = false;
      }

      if (value.length >= config.ops.search.minChars && dataChanged) {
        // We set processing flag to true in order for the UI to display a 'loading' icon
        setGizmoSearchFeatures({
          processing: true,
        });

        // We create and empty array for the searchResultsWithData, the cache
        const searchResultsWithData: {
          searchResult: ISearchResult;
          // data: IFilledFormRowItem;
          data: any;
        }[] = [];

        // We call the function in order to clear the cache
        setSearchResultsWithData(searchResultsWithData);

        const flux = gizmo.getFlux();

        if (flux) {
          const result = await flux.interpolate({
            script: search,
          });
          // We're sending the root gizmo fid so the server knows which RootGizmo is conducting
          // the search
          const rootGizmoFid = gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoFid',
          });

          if (result !== undefined && rootGizmoFid) {
            const searchFilledFormsParams: {
              gizmoConfig: IGizmo;
              rootGizmoFid: string;
              filterSearch?: string;
              textSearch?: string;
            } = {
              gizmoConfig: config,
              rootGizmoFid,
            };
            if (config.ops.search.fluxSearch) {
              searchFilledFormsParams.filterSearch = result;
            } else if (config.ops.search.textSearch) {
              searchFilledFormsParams.textSearch = result;
            }

            // We're going to look on the server for filled forms, right now we only
            // support looking by filter, aka, flux search. We're going to get the results
            // in the filledFormsIndex scheme in order to standarize it
            const resultsIndex = await dao.searchFilledForms(
              searchFilledFormsParams
            );

            if (resultsIndex.rows.length === 0) {
              // If we don't have results we populate the errorMessage
              setGizmoSearchFeatures({
                processing: false,
                message: 'Sin resultados para la búsqueda',
                messageType: 'warning',
                results: [],
              });
              gizmo.setExtraErrors([]);
            } else {
              const searchResults: ISearchResult[] = [];

              for (const filledForm of resultsIndex.rows) {
                const values: string[] = [];
                const answers: any = {};

                for (const column of config.ops.search.columns) {
                  if (filledForm.answers) {
                    const answer = filledForm.answers[column.f_id];

                    if (
                      answer &&
                      answer[0] &&
                      answer[0]['value'] !== undefined
                    ) {
                      values.push(answer[0]['value']);
                    }
                  }
                }

                if (config.ops.search.metadata) {
                  for (const column of config.ops.search.metadata) {
                    const answer = filledForm.answers[column.f_id];

                    if (
                      answer &&
                      answer[0] &&
                      answer[0]['value'] !== undefined
                    ) {
                      answers[column.f_id] = answer;
                    }
                  }
                }

                const v = values.join(' ');

                const option: any = {
                  f_id: filledForm.filledFormId,
                  value: v,
                  formId: filledForm.formId,
                  id: filledForm.id,
                  version: filledForm.version,
                  status: filledForm.status,
                };
                if (config.ops.search.metadata) {
                  option.answers = answers;
                }
                if (filledForm.associations) {
                  option.associations = filledForm.associations;
                }

                searchResults.push(option);
                searchResultsWithData.push({
                  searchResult: option,
                  data: filledForm,
                });
              }

              // If we have results we send the results to the platform layer through the observable
              if (searchResults.length > 0) {
                setGizmoSearchFeatures({
                  processing: false,
                  results: searchResults,
                });
                setSearchResultsWithData(searchResultsWithData);

                if (
                  config.ops.search.searchResultSelectSingleResult === true &&
                  searchResults.length === 1
                ) {
                  // If it's configured and we only have one search result we're going to select it
                  await selectGizmoSearchResult({
                    searchResult: searchResults[0],
                    context: {
                      gizmo,
                      callbacks: gizmo.getCallbacks(),
                      searchResultsWithData,
                      dao,
                    },
                  });
                }
              } else {
                // If we don't have results we populate the errorMessage
                setGizmoSearchFeatures({
                  processing: false,
                  message: 'Sin resultados para la búsqueda',
                  messageType: 'warning',
                  results: [],
                });
                gizmo.setExtraErrors([]);
              }
            }
          }
        }
      } else {
        if (
          gizmoSearchFeatures.results &&
          gizmoSearchFeatures.results.length > 0
        ) {
          const features: ISearchFeatures = {
            processing: false,
            results: [],
          };
          if (
            config.ops.search.searchResultSelectedMessage &&
            dataChanged === false
          ) {
            features.message = config.ops.search.searchResultSelectedMessage;
            features.messageType = 'success';
          }

          // We're arriving here if the user is selecting a result.
          setGizmoSearchFeatures(features);
        }
      }
    }

    if (
      config.type === 'text_field' &&
      config.ops?.search?.minChars &&
      config.ops.search.columns &&
      gizmo &&
      dao &&
      !Array.isArray(data) &&
      data.v &&
      config.ops.search.columnSearch?.columns
    ) {
      const value = data.v.toString();
      let dataChanged = true;

      // If previousData is defined we check if the value has changed
      if (
        previousData &&
        !Array.isArray(previousData) &&
        previousData.v === value
      ) {
        // If it didn't change it means we're selecting a result
        dataChanged = false;
      }

      if (
        value.length >= config.ops.search.minChars &&
        dataChanged &&
        config.ops.search.rootGizmoFid
      ) {
        // We set processing flag to true in order for the UI to display a 'loading' icon
        setGizmoSearchFeatures({
          processing: true,
        });

        // We create and empty array for the searchResultsWithData, the cache
        const searchResultsWithData: {
          searchResult: ISearchResult;
          // data: IFilledFormRowItem;
          data: any;
        }[] = [];

        // We call the function in order to clear the cache
        setSearchResultsWithData(searchResultsWithData);

        const searchColumns: any[] = [];
        for (const column of config.ops.search.columnSearch.columns) {
          searchColumns.push({
            values: [value],
            gizmoFid: column.columnGizmoFid,
          });
        }

        const resultsIndex = await dao.columnSearch({
          rootGizmoFid: config.ops.search.rootGizmoFid,
          searchColumns,
          logicOperator: config.ops.search.columnSearch.logicOperator,
          gizmoConfig: config,
        });

        if (resultsIndex?.rows.length === 0) {
          // If we don't have results we populate the errorMessage
          setGizmoSearchFeatures({
            processing: false,
            message: 'Sin resultados para la búsqueda',
            messageType: 'warning',
            results: [],
          });
          gizmo.setExtraErrors([]);
        } else if (resultsIndex && resultsIndex.rows.length > 0) {
          const searchResults: ISearchResult[] = [];

          resultsIndexToSearchResult({
            searchResults,
            searchResultsWithData,
            resultsIndex,
            config,
          });

          // If we have results we send the results to the platform layer through the observable
          if (searchResults.length > 0) {
            setGizmoSearchFeatures({
              processing: false,
              results: searchResults,
            });
            setSearchResultsWithData(searchResultsWithData);

            if (
              config.ops.search.searchResultSelectSingleResult === true &&
              searchResults.length === 1
            ) {
              // If it's configured and we only have one search result we're going to select it
              await selectGizmoSearchResult({
                searchResult: searchResults[0],
                context: {
                  gizmo,
                  callbacks: gizmo.getCallbacks(),
                  searchResultsWithData,
                  dao,
                },
              });
            }
          } else {
            // If we don't have results we populate the errorMessage
            setGizmoSearchFeatures({
              processing: false,
              message: 'Sin resultados para la búsqueda',
              messageType: 'warning',
              results: [],
            });
            gizmo.setExtraErrors([]);
          }
        }
      } else {
        if (
          gizmoSearchFeatures.results &&
          gizmoSearchFeatures.results.length > 0
        ) {
          const features: ISearchFeatures = {
            processing: false,
            results: [],
          };
          if (
            config.ops.search.searchResultSelectedMessage &&
            dataChanged === false
          ) {
            features.message = config.ops.search.searchResultSelectedMessage;
            features.messageType = 'success';
          }

          // We're arriving here if the user is selecting a result.
          setGizmoSearchFeatures(features);
        }
      }
    }

    if (
      config.type === 'text_field' &&
      config.ops?.serverScript?.run?.stage === 'onSetData'
    ) {
      // We're going to run the server script only when data chanced
      await gizmo.serverScript();
    }

    // We tried this in order to run the server script only when data changed
    // if (
    //   config.type === 'text_field' &&
    //   config.ops?.serverScript?.run?.stage === 'onSetData' &&
    //   ((
    //   previousData &&
    //   data &&
    //   !Array.isArray(previousData) &&
    //   !Array.isArray(data) &&
    //   data.v !== previousData.v
    //   ) || ( previousData === undefined && data !== undefined ))
    // ) {
    //   // We're going to run the server script only when data chanced
    //   await gizmo.serverScript();
    // }

    resolve();
  });
}

interface IResultsIndexToSearchResultParams {
  resultsIndex: IIndex;
  config: IGizmo;
  searchResults: ISearchResult[];
  searchResultsWithData: {
    searchResult: ISearchResult;
    data: any;
  }[];
}
function resultsIndexToSearchResult({
  resultsIndex,
  config,
  searchResults,
  searchResultsWithData,
}: IResultsIndexToSearchResultParams) {
  if (config.ops?.search?.columns) {
    for (const filledForm of resultsIndex.rows) {
      const values: string[] = [];
      const answers: any = {};

      for (const column of config.ops.search.columns) {
        if (filledForm.answers) {
          const answer = filledForm.answers[column.f_id];

          if (answer && answer[0] && answer[0]['value'] !== undefined) {
            values.push(answer[0]['value']);
          }
        }
      }

      if (config.ops.search.metadata) {
        for (const column of config.ops.search.metadata) {
          const answer = filledForm.answers[column.f_id];

          if (answer && answer[0] && answer[0]['value'] !== undefined) {
            answers[column.f_id] = answer;
          }
        }
      }

      const v = values.join(' ');

      const option: any = {
        f_id: filledForm.filledFormId,
        value: v,
        formId: filledForm.formId,
        id: filledForm.id,
        version: filledForm.version,
        status: filledForm.status,
      };
      if (config.ops.search.metadata) {
        option.answers = answers;
      }
      if (filledForm.associations) {
        option.associations = filledForm.associations;
      }

      searchResults.push(option);
      searchResultsWithData.push({
        searchResult: option,
        data: filledForm,
      });
    }
  }
}

export default setData;
